<div class="visually-hidden" aria-live="polite" data-testid="alert-live-status">{{ liveStatus }}</div>
@if (!isLoading && !loadError) {
  <div class="alert-page">
    <header class="alert-header">
      <a class="back-link" routerLink="/alerts"><mat-icon>arrow_back</mat-icon>{{ 'common.labels.allAlerts' | transloco }}</a>
    </header>
    <div class="alert-layout">
      <div class="alert-main">
        <!-- Alert details -->
        <mat-card class="alert-card">
          <mat-card-content>
            <div class="card-title-row">
              <!-- The page's only heading, so it's the h1 (styled like the other card titles) -->
              <div class="title-with-copy">
                <h1 class="card-title">{{ 'common.labels.alertDetails' | transloco }}</h1>
                <lib-copy-to-clipboard [value]="alert.id" [tooltip]="'common.labels.copyAlertId' | transloco" data-testid="alert-copy-id"></lib-copy-to-clipboard>
              </div>
              <button mat-stroked-button color="primary" (click)="navigateToLogs()" data-testid="alert-logs">
                <mat-icon>summarize</mat-icon>
                {{ 'common.labels.logs' | transloco }}
              </button>
            </div>
            <p class="description" data-testid="alert-description">{{ alert.description }}</p>
          </mat-card-content>
        </mat-card>

        <!-- Related orders -->
        <mat-card class="alert-card">
          <mat-card-content>
            <div class="card-title-row">
              <h2 class="card-title">{{ (orders.length === 1 ? 'common.labels.relatedOrder' : 'common.labels.relatedOrders') | transloco }}</h2>
              <!-- One order: its button sits in the header. Several: each row keeps its own and the header shows the count. -->
              @if (orders.length === 1) {
                <a mat-stroked-button color="primary" [routerLink]="['/orders', orders[0].orderID]" data-testid="alert-view-order">{{ 'common.labels.viewOrder' | transloco }}</a>
              } @else if (orders.length > 1) {
                <span class="count-badge">{{ 'common.labels.linkedCount' | transloco: { count: orders.length } }}</span>
              }
            </div>
            <div class="order-list">
              @for (item of orders; track item.orderID) {
                <div class="order-row" [attr.data-testid]="'alert-order-' + item.orderID">
                  <div class="order-summary">
                    @if (item.order; as order) {
                      <div class="detail-grid order-details">
                        <div>
                          <span class="detail-label">{{ 'common.labels.referenceNumber' | transloco }}</span>
                          <span class="detail-value">{{ order.referenceNumber || '—' }}</span>
                        </div>
                        <div>
                          <span class="detail-label">{{ 'common.labels.orderType' | transloco }}</span>
                          <span class="detail-value">{{ order.orderTypeID | enumLabel:'orderType':order.orderType?.name }}</span>
                        </div>
                        <!-- The code (BC), not the full name: "British Columbia, CA" crowds the row -->
                        <div>
                          <span class="detail-label">{{ 'common.labels.alertJurisdiction' | transloco }}</span>
                          <span class="detail-value">{{ order.jurisdiction || order.displayJurisdiction }}, {{ order.country }}</span>
                        </div>
                        <div>
                          <span class="detail-label">{{ 'common.labels.status' | transloco }}</span>
                          <span class="detail-value">{{ order.orderStatusTypeID | enumLabel:'orderStatusType' }}</span>
                        </div>
                        <div>
                          <span class="detail-label">{{ 'common.labels.requested' | transloco }}</span>
                          <span class="detail-value">{{ (order.dateRequested | localizedDate:'yyyy-MM-dd') || '—' }}</span>
                        </div>
                      </div>
                    } @else {
                      <div class="detail-grid order-details">
                        <div>
                          <span class="detail-label">{{ 'common.labels.orderId' | transloco }}</span>
                          <span class="detail-value">{{ item.orderID }}</span>
                        </div>
                      </div>
                      <p class="card-subtitle order-unavailable">{{ 'alertInfo.thisOrderCouldnTBeLoaded' | transloco }}</p>
                    }
                  </div>
                  <!-- With one order the button is in the card header instead -->
                  @if (orders.length > 1) {
                    <a mat-stroked-button color="primary" [routerLink]="['/orders', item.orderID]">{{ 'common.labels.viewOrder' | transloco }}</a>
                  }
                </div>
              } @empty {
                <p class="empty-state">{{ 'common.labels.noOrdersAreLinkedToThis' | transloco }}</p>
              }
            </div>
          </mat-card-content>
        </mat-card>

        <!-- Activity: the conversation with the client plus the entries the API records when the alert changes -->
        <mat-card class="alert-card">
          <mat-card-content>
            <div class="card-title-row">
              <h2 class="card-title">{{ 'common.labels.activity' | transloco }}</h2>
            </div>
            <div class="note-list">
              @for (note of alert.notes; track note.id) {
                <!-- Activity the API recorded (assigned, resolved, severity changed...): a quiet line, never deletable -->
                @if (note.isSystemGenerated) {
                  <div class="activity" [attr.data-testid]="'alert-activity-' + note.id">
                    <mat-icon class="activity-icon">history</mat-icon>
                    <span class="activity-text">{{ note.note }}</span>
                    <span class="note-date">{{ note.added | localizedDate:'yyyy-MM-dd HH:mm' }}</span>
                  </div>
                } @else {
                  <div class="note" [attr.data-testid]="'alert-note-' + note.id">
                    <div class="note-body">
                      <!-- Date on the far right, lined up with the activity entries' dates; delete sits just before it.
                           Admins can remove any typed note; it's confirmed first. -->
                      <div class="note-meta">
                        <!-- A small icon beside the name says who wrote it: RegHub staff get the Contact Us headset, client users a profile icon -->
                        <span class="note-author">
                          <mat-icon class="note-author-icon" [class.note-author-icon-reghub]="isRegHubNote(note)">{{ isRegHubNote(note) ? 'support_agent' : 'account_circle' }}</mat-icon>
                          {{ noteAuthor(note) }}
                        </span>
                        <button mat-icon-button class="note-delete" (click)="deleteNote(note)" [disabled]="deletingNoteID === note.id"
                          [attr.aria-label]="'common.labels.deleteNote' | transloco" [matTooltip]="'common.labels.deleteNote' | transloco">
                          <mat-icon>delete_outline</mat-icon>
                        </button>
                        <span class="note-date">{{ note.added | localizedDate:'yyyy-MM-dd HH:mm' }}</span>
                      </div>
                      <p class="note-text">{{ note.note }}</p>
                    </div>
                  </div>
                }
              } @empty {
                <p class="empty-state">{{ 'alertInfo.noActivityYet' | transloco }}</p>
              }
            </div>
            <div class="composer">
              <mat-form-field class="note-composer" appearance="outline" subscriptSizing="dynamic">
                <mat-label>{{ 'common.labels.addANote' | transloco }}</mat-label>
                <textarea #noteField matInput [(ngModel)]="newNote" [rows]="2" data-testid="alert-note-field"
                  [placeholder]="'alertInfo.replyToTheClientOrRecord' | transloco"></textarea>
              </mat-form-field>
              <div class="composer-footer">
                <button mat-flat-button color="primary" (click)="sendNote()" data-testid="alert-note-send"
                  [disabled]="isSendingNote || !newNote.trim()">
                  <mat-icon>add</mat-icon>
                  {{ (isSendingNote ? 'common.labels.addingNote' : 'common.labels.addNote') | transloco }}
                </button>
              </div>
            </div>
          </mat-card-content>
        </mat-card>
      </div>

      <!-- Admin summary: who the alert is for and where it stands. Assignment is the client's to manage, so it's read-only here. -->
      <aside class="alert-actions" aria-labelledby="alert-summary-title">
        <mat-card class="alert-card">
          <mat-card-content>
            <h2 class="card-title" id="alert-summary-title">{{ 'common.labels.summary' | transloco }}</h2>
            <dl class="summary-facts">
              <div>
                <dt class="detail-label">{{ 'common.labels.corporation' | transloco }}</dt>
                <dd class="summary-value">
                  <a [routerLink]="['/corporations', alert.corporationID]" data-testid="alert-corporation">{{ corporation?.name ?? alert.corporationID }}</a>
                </dd>
              </div>
              <div>
                <dt class="detail-label">{{ 'common.labels.alertRaised' | transloco }}</dt>
                <dd class="summary-value" data-testid="alert-raised">{{ alert.added | localizedDate:'yyyy-MM-dd HH:mm' }}</dd>
              </div>
              <div>
                <dt class="detail-label">{{ 'common.labels.alertType' | transloco }}</dt>
                <dd class="summary-value" data-testid="alert-type-name">{{ alert.alertTypeID | enumLabel:'alertType':alertTypeName }}</dd>
              </div>
              @if (alert.jurisdiction) {
                <div>
                  <dt class="detail-label">{{ 'common.labels.alertJurisdiction' | transloco }}</dt>
                  <dd class="summary-value" data-testid="alert-jurisdiction">{{ alert.jurisdiction }}</dd>
                </div>
              }
              @if (subject) {
                <div>
                  <dt class="detail-label">{{ 'common.labels.trackedItem' | transloco }}</dt>
                  <dd class="summary-value" data-testid="alert-subject">{{ subject }}</dd>
                </div>
              }
              @if (alert.trackedAlertEntity; as entity) {
                <div>
                  <!-- Admins need to know when a client has stopped tracking; the label says so, as on the client's alert page -->
                  <dt class="detail-label">{{ (isTracked ? 'common.labels.trackingUntil' : 'common.labels.trackingEnded') | transloco }}</dt>
                  <dd class="summary-value" data-testid="alert-tracked-until">
                    {{ (entity.trackingEndDate | localizedDate:'yyyy-MM-dd') || ('common.labels.ongoing' | transloco) }}
                  </dd>
                </div>
              }
              <div>
                <dt class="detail-label">{{ 'common.labels.assignedTo' | transloco }}</dt>
                <dd class="summary-value" data-testid="alert-assignee">{{ assignedUserName }}</dd>
              </div>
            </dl>
            <!-- Severity and status stay editable for admins; the selected value shows as the same pill as the list -->
            <div class="sidebar-section triage">
              <mat-form-field class="full-width" appearance="outline" subscriptSizing="dynamic">
                <mat-label>{{ 'common.labels.severity' | transloco }}</mat-label>
                <mat-select #severitySelect [value]="alert.alertSeverityID" (selectionChange)="onSeverityChange($event.value)"
                  [disabled]="isSaving" data-testid="alert-severity">
                  <mat-select-trigger><span class="alert-pill" [ngClass]="severityClass">{{ alert.alertSeverityID | enumLabel:'alertSeverity' }}</span></mat-select-trigger>
                  @for (option of alertSeverities; track option.value) {
                    <mat-option [value]="option.value">{{ option.value | enumLabel:'alertSeverity' }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <mat-form-field class="full-width" appearance="outline" subscriptSizing="dynamic">
                <mat-label>{{ 'common.labels.status' | transloco }}</mat-label>
                <mat-select #statusSelect [value]="alert.alertStatusID" (selectionChange)="onStatusChange($event.value)"
                  [disabled]="isSaving" data-testid="alert-status">
                  <mat-select-trigger><span class="alert-pill" [ngClass]="statusClass">{{ alert.alertStatusID | enumLabel:'alertStatus' }}</span></mat-select-trigger>
                  @for (option of alertStatuses; track option.value) {
                    <mat-option [value]="option.value">{{ option.value | enumLabel:'alertStatus' }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>
          </mat-card-content>
        </mat-card>
      </aside>
    </div>
  </div>
}

@if (isLoading || loadError) {
  <h1 class="visually-hidden">{{ 'common.labels.alertDetails' | transloco }}</h1>
}

@if (loadError) {
  <div class="padding">
    <lib-placeholder [iconName]="'cancel'" [iconClass]="'icon-error'" [message]="'common.messages.errorLoadingAlert' | transloco" [condition]="true"></lib-placeholder>
  </div>
}

@if (isLoading) {
  <div class="loading-spinner-overlay">
    <mat-spinner></mat-spinner>
  </div>
}
